import {Component, ElementRef, EventEmitter, Input, OnInit, Output, Renderer2, ViewChild} from "@angular/core";
import {Filter,ListView,TypeComparaison,TypeFilter} from "@domain/common/list-view";
import {ListItem} from "@domain/common/list-view/list-item";
import {ListViewItem} from "@domain/common/list-view/list-view-item";

/**
 * Composant d'affichage des filtres de liste
 */
@Component({
    host: {'data-test-id': 'list-view-search-chip'},
    selector: 'list-view-search-chip',
    templateUrl: './list-view-search-chip.component.html',
    styleUrls: ['./list-view-search-chip.component.scss']
})
export class ListViewSearchChipComponent<T extends ListItem,K extends ListViewItem<T>> implements OnInit {
    /** CONSTANTES */
    readonly IS_GLOBAL: string = 'isGlobal';

    /** Liste concernée */
    @Input() liste: ListView<T,K>;

    /** Filtre concerné */
    @Input() filter: Filter;

    /** Filtre correspondant dans la liste des possibles */
    matchingFilter: Filter;

    /** Méthode pour déterminer la visibilité d'une pastille */
    @Input() isChipVisible: (item: Filter) => boolean;

    /** Évènement d'appui sur la touche Enter */
    @Output() enterPressedInFilter: EventEmitter<void> = new EventEmitter<void>();

    /** Évènement de demande de rafraichissement des données */
    @Output() onRefreshData: EventEmitter<void> = new EventEmitter<void>();

    /** Évènement de déploiement du panneau des filtres */
    @Output() unfoldFiltersPanel: EventEmitter<Filter> = new EventEmitter<Filter>();

    /** Composant d'input pour l'édition d'une valeur */
    @ViewChild('filterEditInput') filterEditInput!: ElementRef;

    /** Composant invisible pour déterminer la largeur dynamique de l'input */
    @ViewChild('filterEditInputWidthCalculator') filterEditInputWidthCalculator!: ElementRef;

    /** Sauvegarde de la valeur avant édition */
    private backupValue: string;

    /** Flag d'édition active */
    isEditActive: boolean = false;

    /** Flag de blocage de l'appel à la méthode de perte de focus */
    ignoreFocusOut: boolean = false;

    /**
     * Constructeur
     */
    constructor(private renderer: Renderer2) {}

    /**
     * Initialisation du composant
     */
    ngOnInit() {
        //Récupération du filtre correspondant dans la liste
        this.matchingFilter = this.matchFilterWithSelectedFilter(this.filter);

        //Détermination à l'avance de la largeur de l'input d'édition (évite l'impression de clignotement si le dimensionnement a lieu lors du clic)
        this.adjustWidth();
    }

    /**
     * Fait la correspondance entre un filtre actif et son équivalent dans la liste des possibilités
     *
     * @param filter le filtre concerné
     */
    private matchFilterWithSelectedFilter(filter: Filter): Filter {
        //Recherche dans les filtres pré-existants
        let filterFound: Filter = this.liste.listeFilters.find(f => f.clef == filter.clef && f.valeur?.replace(/%/g,"") == filter.valeur?.replace(/%/g,""));

        //Si il n'y a aucun résultat, alors c'est un filtre global
        if (!filterFound) {
            //On le retrouve dans la liste des filtres sélectionnés
            filterFound = this.liste.listeSelectedFilters.find(f => f.clef == filter.clef && f.valeur?.replace(/%/g,"") == filter.valeur?.replace(/%/g,""));

            //On lui rajoute une clé de différenciation spécifique
            filterFound[this.IS_GLOBAL] = true;
        }

        //Retourne le filtre trouvé
        return filterFound;
    }

    /**
     * Intercepte l'évènement du clavier pour l'empêcher de remonter jusqu'au mat-chip-list et empêche ainsi la suppression de la pastille lors de l'utilisation de la touche Backspace dans l'input
     *
     * @param event évènement du clavier
     */
    preventEventFromGoingUpToMatChipList(event: KeyboardEvent): void {
        //Interruption de la propagation de l'évènement
        event.stopPropagation();
    }

    /**
     * Ajuste dynamiquement la largeur de l'input d'édition
     */
    adjustWidth(): void {
        //Sortie du cycle standard pour laisser le temps au span de changer de taille avant d'en prendre la mesure
        setTimeout(() => {
            //Application de la nouvelle largeur
            this.renderer.setStyle(this.filterEditInput.nativeElement, 'width', (this.filterEditInputWidthCalculator.nativeElement.offsetWidth + 10) + 'px');
        });
    }

    /**
     * Pression sur la touche Entrée
     */
    onPressEnterInFilter(): void {
        //Blocage de la perte de focus
        this.ignoreFocusOut = true;

        //Fin du mode édition
        this.isEditActive = false;

        //Si le filtre est un filtre global ou une valeur d'affichage spécifique
        if (this.matchingFilter[this.IS_GLOBAL] || this.matchingFilter.displayedValeur) {
            //Conversion de la valeur technique en se basant sur la valeur renseignée
            this.matchingFilter.valeur = `${this.matchingFilter.valeur?.startsWith('%') ? '%' : ''}${this.matchingFilter.displayedValeur}${this.matchingFilter.valeur?.endsWith('%') ? '%' : ''}`;
        }

        //Si on a bien une valeur dans le filtre qui va être appliqué
        if (!!this.matchingFilter.valeur) {
            //Emission de la demande de recherche
            this.enterPressedInFilter.emit();
        }

        //Réactivation de la perte de focus en dehors du cycle standard (pour laisser le temps à l'input de complètement disparaitre)
        setTimeout(() => this.ignoreFocusOut = false);
    }

    /**
     * Clic sur un filtre
     *
     * @param filter filtre concerné
     */
    onFilterClick(filter: Filter): void {
        //Si le filtre est concerné par l'édition directe dans l'input
        if (filter.type == TypeFilter[TypeFilter.STRING]
            || filter.type == TypeFilter[TypeFilter.LONG]
            || (filter.typeComparaison == TypeComparaison[TypeComparaison.LIKE] && !filter.listeValues)) {
            //On sauvegarde la valeur actuelle
            this.backupValue = this.matchingFilter[this.IS_GLOBAL] || this.matchingFilter.displayedValeur ? this.matchingFilter.displayedValeur : this.matchingFilter.valeur;

            //On active l'édition
            this.isEditActive = true;

            //On ajuste la largeur de l'input
            this.adjustWidth();

            //Déplacement du curseur texte dans le champ de l'input (hors cycle standard pour laisser le temps à l'input de s'afficher)
            setTimeout(() => this.filterEditInput.nativeElement.focus());
        } else {
            //Sinon, on déplie les filtres
            this.unfoldFiltersPanel.emit(filter);
        }
    }

    /**
     * Désactivation du mode édition
     */
    onEditModeClose(): void {
        //Si l'appel à la perte de focus est autorisé
        if (!this.ignoreFocusOut) {
            //On désactive l'édition
            this.isEditActive = false;

            //Si le filtre est un filtre global ou une valeur d'affichage spécifique
            if (this.matchingFilter[this.IS_GLOBAL] || this.matchingFilter.displayedValeur) {
                //On remet la valeur initiale dans la valeur d'affichage
                this.matchingFilter.displayedValeur = this.backupValue;
            } else {
                //Sinon, on remet la valeur initiale standard
                this.matchingFilter.valeur = this.backupValue;
            }
        }
    }

    /**
     * Suppression d'un filtre
     * @param filter filtre à supprimer
     */
    removeFilter(filter: Filter) {
        //Initialisation
        let removeIndex: number;
        let unFilterParmiDautres: Filter;

        //Recherche du filtre dans la liste des filtres sélectionnés.
        //Il est possible d'avoir des doublons de clef, on recherche également avec la valeur.
        if ((removeIndex = this.liste.listeSelectedFilters.findIndex(f => f.clef == filter.clef && f.valeur == filter.valeur)) >= 0) {
            //Suppression du filtre
            this.liste.listeSelectedFilters.splice(removeIndex,1);
        }

        //Si la clef du filtre contient une virgule, c'est qu'on a tapé le filtre directement dans la barre de recherche.
        //On n'a donc pas besoin de désélectionner de filtre.
        if (filter.clef.indexOf(',') == -1) {
            //Recherche du filtre dans la liste de tous les filtres disponibles, on n'a pas de doublon dans ce cas.
            if ((unFilterParmiDautres = this.liste.listeFilters.find(f => f.clef == filter.clef))) {
                //Purge de toutes les valeurs dans le filtre afin de le désactiver pour la recherche tout en le laissant sélectionné
                unFilterParmiDautres.valeur = undefined;
                unFilterParmiDautres.listeObjects = undefined;
                unFilterParmiDautres.dateDebut = undefined;
                unFilterParmiDautres.dateFin = undefined;

                //Si le filtre comporte une methode de suppression
                if (filter.removeMethod) {
                    //Appel de la methode avec le filtre
                    filter.removeMethod(unFilterParmiDautres);
                }
            }
        } else {
            //Si le filtre comporte une methode de suppression
            if (filter.removeMethod) {
                //Appel de la methode avec le filtre contenant les valeurs tapées dans la barre de recherche
                filter.removeMethod(filter);
            }
        }

        //Réinitialisation de previousLength et hasNewData pour permettre le chargement de toutes les pages
        this.liste.previousLength = -1;
        this.liste.hasNewData = true;

        //Rafraichissement de la liste
        this.onRefreshData.emit();
    }
}
